"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import DownloadDialog from "@/components/Library/DownloadDialog";
import NPdfDialog from "@/components/Library/NPdfDialog";
import { cn } from "@/lib/utils";
import { Pagination } from "@bookips/sds";
import { useCallback, useEffect } from "react";
import { mixpanelEvent } from "src/lib/mixpanels/mixpanel";
import { ProductListHeader } from "./ProductListHeader";
import { ProductListItem, ProductListItemSkeleton } from "./ProductListItem";
import { useProductItemHandlers } from "./useProductItemHandlers";
/**
 * 빈 상태 컴포넌트 - Mixpanel 이벤트 트래킹 포함
 */ const EmptyState = (param)=>{
    let { message , pageType  } = param;
    useEffect(()=>{
        const position = pageType === "system" ? "시스템 카테고리" : "에디션 카테고리";
        mixpanelEvent("No content to display", {
            position
        });
    }, [
        pageType
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: "flex items-center justify-center py-12",
        children: /*#__PURE__*/ _jsx("p", {
            className: "text-gray-500",
            children: message
        })
    });
};
/**
 * 1depth용 상품 리스트 컴포넌트
 * - 헤더 (총 개수 + 정렬)
 * - 플랫 리스트
 * - 페이지네이션
 */ export const ProductList = (param)=>{
    let { className , items , totalCount , sortOptions , selectedSort , onSortChange , pagination , isLoading , emptyMessage ="상품이 없습니다." , scrollToTopOnPageChange =true , purchaseUrl , pageType ="edition"  } = param;
    // 페이지 변경 핸들러 (스크롤 상단 이동 포함)
    const handlePageChange = useCallback((page)=>{
        pagination === null || pagination === void 0 ? void 0 : pagination.onPageChange(page);
        if (scrollToTopOnPageChange) {
            window.scrollTo({
                top: 0,
                behavior: "instant"
            });
        }
    }, [
        pagination,
        scrollToTopOnPageChange
    ]);
    return /*#__PURE__*/ _jsxs("section", {
        className: cn("flex flex-col", className),
        children: [
            /*#__PURE__*/ _jsx(ProductListHeader, {
                totalCount: totalCount,
                sortOptions: sortOptions,
                selectedSort: selectedSort,
                onSortChange: onSortChange,
                pageType: pageType
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex flex-col max-sm:-mx-5",
                children: isLoading ? // 로딩 스켈레톤
                /*#__PURE__*/ _jsx("div", {
                    className: "flex flex-col",
                    children: Array.from({
                        length: 5
                    }).map((_, index)=>/*#__PURE__*/ _jsx(ProductListItemSkeleton, {
                            variant: "default",
                            className: cn("p-5", index < 4 && "border-b border-gray-100")
                        }, index))
                }) : items.length === 0 ? // 빈 상태
                /*#__PURE__*/ _jsx(EmptyState, {
                    message: emptyMessage,
                    pageType: pageType
                }) : // 아이템 리스트
                /*#__PURE__*/ _jsx(ProductListWithFreeTrialDialog, {
                    items: items,
                    purchaseUrl: purchaseUrl,
                    pageType: pageType
                })
            }),
            pagination && pagination.totalPages > 1 && !isLoading && items.length > 0 && /*#__PURE__*/ _jsx("div", {
                className: "mt-6 mb-6 flex justify-center",
                children: /*#__PURE__*/ _jsx(Pagination, {
                    currentPage: pagination.currentPage,
                    totalPages: pagination.totalPages,
                    onPageChange: handlePageChange,
                    size: "lg",
                    arrow: true,
                    ellipsis: true,
                    shape: "rounded"
                })
            })
        ]
    });
};
/**
 * 무료체험 및 이용권 다이얼로그가 연결된 상품 리스트
 * - useProductItemHandlers 훅을 통해 핸들러 및 다이얼로그 관리
 */ const ProductListWithFreeTrialDialog = (param)=>{
    let { items , purchaseUrl , pageType ="edition"  } = param;
    const { createSampleClickHandler , createCtaClickHandler , createPdfDownloadHandler , isDownloadDialogOpen , downloadData , isFirstUseForDownload , closeDownloadDialog , handleDownloadComplete , getAcquiredLicenseInfo , nPdfOpen , closeNPdfDialog , nPdfCount , nPdfActionType , handleNPdfDirectAction , handleNPdfGoToDetail , nPdfDetailUrl  } = useProductItemHandlers({
        purchaseUrl,
        pageType
    });
    const position = pageType === "system" ? "시스템 카테고리" : "에디션 카테고리";
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            items.map((item, index)=>/*#__PURE__*/ _jsx(ProductListItem, {
                    product: item,
                    className: index < items.length - 1 ? "border-b border-gray-100" : undefined,
                    onSampleClick: createSampleClickHandler(item),
                    onCtaClick: createCtaClickHandler(item),
                    onPdfDownloadClick: createPdfDownloadHandler(item),
                    pageType: pageType,
                    acquiredLicenseInfo: getAcquiredLicenseInfo(item.id)
                }, item.id)),
            downloadData && /*#__PURE__*/ _jsx(DownloadDialog, {
                isOpen: isDownloadDialogOpen,
                close: closeDownloadDialog,
                selectedData: downloadData,
                selectedFileType: "pdf",
                onDownloadComplete: handleDownloadComplete,
                getOpenViewer: {
                    isFirstUse: isFirstUseForDownload
                },
                skipConfirm: !isFirstUseForDownload,
                position: position
            }),
            /*#__PURE__*/ _jsx(NPdfDialog, {
                open: nPdfOpen,
                onOpenChange: (open)=>{
                    if (!open) closeNPdfDialog();
                },
                count: nPdfCount !== null && nPdfCount !== void 0 ? nPdfCount : 0,
                actionType: nPdfActionType,
                onDirectAction: handleNPdfDirectAction,
                onGoToDetail: handleNPdfGoToDetail,
                position: position,
                detailUrl: nPdfDetailUrl
            })
        ]
    });
};
